import React, { useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { getTenantSlugsFromHost } from 'services/stytchAuthService';
import { validateTenant } from 'services/b2c/consumerAuthService';
import { clearTenantKeys, clearSecondarySessionKeys } from 'utils/sessionKeys';
import { clearCachedUserMetadata } from 'auth/auth';
import { performLogout } from 'utils/logout';
import Loader from 'wrapper/loader';

// One-shot guard so a failed verify redirects to a clean login exactly once.
// Without it, a genuinely-invalid host would redirect → fail → redirect …
// forever. Lives in sessionStorage (per-tab) and is cleared on any success.
const RESET_GUARD_KEY = 'fm_tenant_gate_reset';

// Reserved subdomains that are NOT client tenants and must bypass the
// client-org tenant check. The platform admin portal is served at
// `platform.<base>` but its organization slug is `mira-platform`, so validating
// the hostname slug (`platform`) as a client org 404s and bounces admins back to
// login. These hosts are gated by their own platform/SSO auth instead.
// Override/extend via VITE_RESERVED_SUBDOMAINS (comma-separated).
const RESERVED_SUBDOMAINS = new Set(
  (import.meta?.env?.VITE_RESERVED_SUBDOMAINS || 'platform')
    .toLowerCase()
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
);

/**
 * Forget every client-side credential/tenant cache. Called when the org
 * verify fails so the next login starts from a truly clean slate — no stale
 * slug, org id, or secondary (tab) session token lingering. Best-effort:
 * storage access can throw in locked-down browsers.
 */
const forgetCachedCreds = () => {
  try {
    clearTenantKeys();
    clearSecondarySessionKeys();
    clearCachedUserMetadata();
  } catch (e) {
    /* best-effort — nothing to recover if storage is unavailable */
  }
};

/**
 * Route-level wrapper that validates org/branch slugs from the hostname
 * BEFORE rendering any child route. Covers the welcome page, B2C login,
 * and B2B login so every public page on a tenant subdomain is gated.
 *
 * - null  = still checking (shows loading)
 * - true  = valid tenant, render children via <Outlet />
 * - false = invalid tenant, clear cached creds + bounce to a clean login
 */
const TenantGate = () => {
  const [tenantValid, setTenantValid] = useState(null);
  const [tenantError, setTenantError] = useState('');

  useEffect(() => {
    const { clientSlug, branchSlug } = getTenantSlugsFromHost();

    // Reserved (non-tenant) subdomain such as the platform admin portal:
    // there is no client org named `platform`, so skip the client-org check
    // and let the platform's own auth handle access. Without this the gate
    // 404s on validate-tenant and bounces admins back to login.
    if (clientSlug && RESERVED_SUBDOMAINS.has(clientSlug.toLowerCase())) {
      if (typeof sessionStorage !== 'undefined') sessionStorage.removeItem(RESET_GUARD_KEY);
      setTenantValid(true);
      return;
    }

    // Tenant switch: if the cached tenant doesn't match THIS host, purge the
    // stale tenant/org context up-front so a not-logged-out session can't leak
    // its tenant into this public login/invitation page.
    if (clientSlug && typeof sessionStorage !== 'undefined') {
      const cachedSlug = sessionStorage.getItem('mira_client_slug');
      if (cachedSlug && cachedSlug.toLowerCase() !== clientSlug.toLowerCase()) {
        clearTenantKeys();
      }
    }

    if (!clientSlug) {
      // No slug in hostname (e.g., localhost) — skip validation
      if (typeof sessionStorage !== 'undefined') sessionStorage.removeItem(RESET_GUARD_KEY);
      setTenantValid(true);
      return;
    }

    let cancelled = false;
    validateTenant(clientSlug, branchSlug)
      .then((resp) => {
        if (cancelled) return;
        const data = resp?.data ?? {};
        if (data.valid) {
          if (typeof sessionStorage !== 'undefined') sessionStorage.removeItem(RESET_GUARD_KEY);
          setTenantValid(true);
        } else {
          setTenantValid(false);
          setTenantError('Invalid organization or branch. Please check your URL.');
        }
      })
      .catch((err) => {
        if (cancelled) return;
        const detail = err?.response?.data?.detail;
        setTenantValid(false);
        setTenantError(detail || 'Organization not found. Please check your URL.');
      });

    return () => { cancelled = true; };
  }, []);

  // Verify failed → forget cached creds and bounce back to a clean login once.
  useEffect(() => {
    if (tenantValid !== false) return;
    forgetCachedCreds();
    if (typeof sessionStorage !== 'undefined' && !sessionStorage.getItem(RESET_GUARD_KEY)) {
      sessionStorage.setItem(RESET_GUARD_KEY, '1');
      window.location.replace(window.location.origin);
    }
  }, [tenantValid]);

  const handleReturnToLogin = () => {
    // Deliberate user action → FULL logout: performLogout() clears client
    // caches AND revokes the HttpOnly SSO session cookies (mira_session /
    // mira_refresh) server-side, then redirects to a clean login. This is the
    // only way to forget the cookie-based creds — JS can't delete HttpOnly
    // cookies. (The automatic failure path above stays cache-only on purpose,
    // so a transient invalid-URL hit can't cascade-logout other valid tabs.)
    performLogout().catch(() => {
      forgetCachedCreds();
      window.location.replace(window.location.origin);
    });
  };

  if (tenantValid === null) {
    return (
      <Loader
        variant="colorful"
        FullScreen={true}
        message="Verifying organization..."
      />
    );
  }

  if (tenantValid === false) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100vh', gap: '1rem' }}>
        <img src="/fundmoreai.png" alt="Fundmore" style={{ height: '48px' }} />
        <h1 style={{ fontSize: '1.5rem', fontWeight: 600, color: '#111827' }}>Invalid Portal URL</h1>
        <p style={{ color: '#6b7280', maxWidth: '400px', textAlign: 'center' }}>{tenantError}</p>
        <button
          type="button"
          onClick={handleReturnToLogin}
          style={{ marginTop: '0.5rem', padding: '0.6rem 1.4rem', fontSize: '0.95rem', fontWeight: 600, color: '#fff', background: '#16a34a', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
        >
          Return to login
        </button>
      </div>
    );
  }

  return <Outlet />;
};

export default TenantGate;
